import * as React from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';

import { cn } from '@wwe-portal/ui/lib/lib';
import { VisuallyHidden } from '../visually-hidden';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogTitle = DialogPrimitive.DialogTitle;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      'z-dialog data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/80',
      className
    )}
    ref={ref}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const dialogContentVariants = cva('', {
  variants: {
    variant: {
      message: 'z-payment',
      dialog: 'z-dialog',
      stepups: 'z-stepups',
    },
    overlay: {
      message: 'z-payment',
      dialog: 'z-dialog',
      stepups: 'z-stepups',
    },
  },
});

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    variant?: 'message' | 'dialog' | 'stepups';
    overlayProps?: React.ComponentPropsWithoutRef<
      typeof DialogPrimitive.Overlay
    >;
    title?: React.ReactNode;
  }
>(({ className, overlayProps, children, variant, title, ...props }, ref) => {
  return (
    <DialogPortal>
      <DialogOverlay
        {...overlayProps}
        className={cn(
          dialogContentVariants({
            overlay: variant,
          }),
          overlayProps?.className
        )}
      />
      <DialogPrimitive.Content
        className={cn(
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[100%] font-poppins text-text300 from-back800 to-back600 from-back200 to-back200 fixed left-[50%] top-[50%] w-[calc(100%-32px)] max-w-80 translate-x-[-50%] translate-y-[-50%] overflow-visible rounded-[10px] bg-gradient-to-r shadow-lg outline-none data-[state=open]:duration-300 data-[state=open]:ease-in md:max-w-[410px] 2xl:max-w-[480px]',
          dialogContentVariants({ variant }),
          className
        )}
        ref={ref}
        {...props}
        aria-describedby={props['aria-describedby'] ?? undefined}
        tabIndex={undefined} // Remove the `tabIndex` attribute from the `DialogContent` element, to prevent it display outline when it's focused
      >
        <VisuallyHidden>
          <DialogTitle>{title ?? 'Dialog Title'}</DialogTitle>
        </VisuallyHidden>
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

export {
  Dialog,
  DialogTitle,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
};
